import { useState, useCallback, useRef, lazy, Suspense } from "react";
import { SmartHeader } from "@/components/SmartHeader";
import { HeroSection } from "@/components/HeroSection";
import { TelegramButton } from "@/components/TelegramButton";
import { Preloader } from "@/components/Preloader";
import { OverlayTransition } from "@/components/OverlayTransition";
import { ScrollReveal } from "@/hooks/useScrollReveal";

/** Thin hairline divider between sections. */
const Divider = () => (
  <div className="mt-12 md:mt-16 px-5 md:px-12">
    <div className="mx-auto h-px max-w-[1200px] bg-white/[0.08]" />
  </div>
);

// Lazy load below-the-fold components to reduce initial JS/CSS bundle
const ImpactSection = lazy(() => import("@/components/ImpactSection").then(m => ({ default: m.ImpactSection })));
const ServicesSection = lazy(() => import("@/components/ServicesSection").then(m => ({ default: m.ServicesSection })));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection").then(m => ({ default: m.TestimonialsSection })));
const FAQSection = lazy(() => import("@/components/FAQSection").then(m => ({ default: m.FAQSection })));
const FooterSection = lazy(() => import("@/components/FooterSection").then(m => ({ default: m.FooterSection })));
const ReviewsOverlay = lazy(() => import("@/components/overlays/ReviewsOverlay").then(m => ({ default: m.ReviewsOverlay })));
const ServicesOverlay = lazy(() => import("@/components/overlays/ServicesOverlay").then(m => ({ default: m.ServicesOverlay })));
const ProjectOverlay = lazy(() => import("@/components/overlays/ProjectOverlay").then(m => ({ default: m.ProjectOverlay })));
const CaseOverlay = lazy(() => import("@/components/overlays/CaseOverlay").then(m => ({ default: m.CaseOverlay })));
const OverlayHeader = lazy(() => import("@/components/overlays/OverlayHeader").then(m => ({ default: m.OverlayHeader })));

type ActiveOverlay = 'reviews' | 'services' | 'project' | 'case' | null;

const Index = () => {
  const [activeOverlay, setActiveOverlay] = useState<ActiveOverlay>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [transitionTick, setTransitionTick] = useState(0);
  const scrollPositionRef = useRef(0);

  /** black -> white curtain, then open the light overlay */
  const runTransition = useCallback((open: () => void) => {
    setTransitionTick((t) => t + 1);
    window.setTimeout(open, 320);
  }, []);

  const handleOpenReviews = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    runTransition(() => setActiveOverlay('reviews'));
  }, [runTransition]);

  const handleOpenServices = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    setActiveOverlay('services');
  }, []);

  const handleOpenProject = useCallback((projectId: string) => {
    setSelectedProjectId(projectId);
    setActiveOverlay('project');
  }, []);

  const handleOpenCase = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    runTransition(() => setActiveOverlay('case'));
  }, [runTransition]);

  const handleCloseOverlay = useCallback(() => {
    setActiveOverlay(null);
    setSelectedProjectId(null);
    // Restore scroll position after overlay closes
    setTimeout(() => {
      window.scrollTo({ top: scrollPositionRef.current, behavior: 'instant' });
    }, 50);
  }, []);

  const handleBackFromProject = useCallback(() => {
    setActiveOverlay('reviews');
    setSelectedProjectId(null);
  }, []);

  const handleNavigate = useCallback((overlay: ActiveOverlay) => {
    if (overlay === null) {
      setActiveOverlay(null);
      setSelectedProjectId(null);
      // Always scroll to absolute top when going to main
      document.body.style.overflow = 'unset';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollPositionRef.current = window.scrollY;
      setActiveOverlay(overlay);
    }
  }, []);

  const scrollToFAQ = useCallback(() => {
    const faqSection = document.getElementById("faq");
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const isOverlayOpen = activeOverlay !== null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Preloader />
      <SmartHeader
        onOpenReviews={handleOpenReviews}
        onOpenServices={handleOpenServices}
        onScrollToFAQ={scrollToFAQ}
        isOverlayOpen={isOverlayOpen}
        onGoToMain={() => handleNavigate(null)}
      />
      
      {/* Section 1: Hero */}
      <HeroSection />
      
      {/* Section 2: Impact */}
      <div className="pt-12 md:pt-16 lg:pt-20">
        <ScrollReveal threshold={0.1}>
          <Suspense fallback={<div className="min-h-[280px]" />}>
            <ImpactSection onOpenCase={handleOpenCase} />
          </Suspense>
        </ScrollReveal>
      </div>

      <Divider />

      {/* Section 3: Services */}
      <div className="pt-12 md:pt-16 lg:pt-20">
        <ScrollReveal threshold={0.1} delay={50}>
          <Suspense fallback={<div className="min-h-[420px]" />}>
            <ServicesSection onOpenProjects={handleOpenReviews} />
          </Suspense>
        </ScrollReveal>
      </div>

      <Divider />

      {/* Section 4: Testimonials */}
      <div className="pt-10 md:pt-14 lg:pt-16">
        <ScrollReveal threshold={0.1} delay={50}>
          <Suspense fallback={<div className="min-h-[320px]" />}>
            <TestimonialsSection />
          </Suspense>
        </ScrollReveal>
      </div>

      <Divider />

      {/* Section 5: FAQ */}
      <div className="pt-10 md:pt-14 lg:pt-16">
        <ScrollReveal threshold={0.1} delay={50}>
          <Suspense fallback={<div className="min-h-[280px]" />}>
            <FAQSection />
          </Suspense>
        </ScrollReveal>
      </div>
      
      {/* Footer */}
      <div className="pt-12 md:pt-16 mt-auto flex-shrink-0">
        <ScrollReveal threshold={0.05} delay={100}>
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <FooterSection onOpenProjects={handleOpenReviews} onScrollToFAQ={scrollToFAQ} />
          </Suspense>
        </ScrollReveal>
      </div>
      
      <TelegramButton />
      <OverlayTransition trigger={transitionTick} />

      {/* Overlays */}
      <Suspense fallback={null}>
        {activeOverlay && (
          <OverlayHeader 
            activeOverlay={activeOverlay}
            onClose={handleCloseOverlay}
            onBack={activeOverlay === 'project' ? handleBackFromProject : undefined}
            onNavigate={handleNavigate}
          />
        )}
        
        <ReviewsOverlay 
          isOpen={activeOverlay === 'reviews'}
          onClose={handleCloseOverlay}
          onOpenProject={handleOpenProject}
        />
        
        <ServicesOverlay 
          isOpen={activeOverlay === 'services'}
          onClose={handleCloseOverlay}
        />
        
        <ProjectOverlay 
          isOpen={activeOverlay === 'project'}
          projectId={selectedProjectId}
          onClose={handleCloseOverlay}
        />

        <CaseOverlay 
          isOpen={activeOverlay === 'case'}
          onClose={handleCloseOverlay}
        />
      </Suspense>
    </div>
  );
};

export default Index;
